import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import css from './subModal.module.css';
import { useTranslation } from 'react-i18next';
import { cancelSubscription } from '../../lib/cancelSubscriptionApi';

const SubModal = ({ isOpen, onClose, authUser, currentSubscription, onSubscriptionCancelled }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  // Track when subscription data is loaded.
  // We don't want to render "Free" until we actually know the subscription status.
  useEffect(() => {
    if (!isOpen) {
      setIsLoadingSubscription(true);
      return;
    }

    const hasStatus =
      currentSubscription &&
      currentSubscription.status !== undefined &&
      currentSubscription.status !== null;

    setIsLoadingSubscription(!hasStatus);
  }, [isOpen, currentSubscription]);

  // Handle visibility with delay for animation
  useEffect(() => {
    if (isOpen) {
      // First make visible (mounts the DOM)
      setIsVisible(true);
      // Then trigger animation after a tiny delay
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShouldAnimate(true);
        });
      });
    } else {
      // Remove animation class immediately
      setShouldAnimate(false);
      // Delay removal to allow exit animation
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 300); // Match transition duration
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Close modal with Escape key and handle body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Small delay to prevent layout shift during animation
      requestAnimationFrame(() => {
        document.body.style.overflow = 'hidden';
      });
    } else {
      // Delay scrollbar restoration until after exit animation completes
      const timer = setTimeout(() => {
        document.body.style.overflow = 'unset';
      }, 300); // Match transition duration
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleEscape);
      };
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleUpgradePlan = () => {
    const isPricingPage = location.pathname === '/pricing';
    
    if (isPricingPage) {
      // Just close modal if already on pricing page
      onClose();
    } else {
      // Close modal and navigate to pricing
      onClose();
      setTimeout(() => {
        navigate('/pricing');
      }, 300); // Wait for modal close animation
    }
  };

  const handleCancelSubscription = async () => {
    if (window.confirm('Are you sure you want to cancel your subscription? You will lose access to premium features.')) {
      displayToast('Processing cancellation...');
      try {
        const result = await cancelSubscription();
        if (result) {
          displayToast('Subscription cancelled successfully');
          // Callback to parent to refresh subscription state
          if (onSubscriptionCancelled) {
            onSubscriptionCancelled();
          }
          // Close modal after a short delay
          setTimeout(() => {
            onClose();
          }, 1000);
        } else {
          displayToast('Error cancelling subscription');
        }
      } catch (error) {
        console.error('Error in cancel subscription:', error);
        displayToast('Error cancelling subscription');
      }
    }
  };

  const displayToast = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000);
  };

  if (!isVisible) return null;

  const isAuthenticated = authUser && authUser.isAuthenticated === true;

  const handleSignIn = () => {
    onClose();
    setTimeout(() => {
      navigate('/signin');
    }, 300);
  };

  const subscriptionStatusValue = Number(currentSubscription?.status);
  const isActiveSubscription = subscriptionStatusValue === 2;

  const nextRenewalRaw = currentSubscription?.next_billing_date || currentSubscription?.expires_at;
  const nextRenewalDate = nextRenewalRaw ? new Date(nextRenewalRaw) : null;
  const nextRenewalLabel =
    nextRenewalDate && !Number.isNaN(nextRenewalDate.getTime()) ? nextRenewalDate.toLocaleDateString() : '-';

  return (
    <>
      {/* Popup Overlay */}
      <div 
        className={`${css.popupOverlay} ${shouldAnimate ? css.active : ''}`} 
        onClick={handleOverlayClick}
      >
        <div className={css.popupContainer} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className={css.popupHeader}>
            <div className={css.popupTitle}>
              <div className={css.popupIcon}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="16" rx="2" ry="2"></rect>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                </svg>
              </div>
              {t("Subscriptions")}
            </div>
            <button className={css.closeBtn} onClick={onClose}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className={css.popupContent}>
            {!isAuthenticated ? (
              /* Not Authenticated View */
              <div className={css.notAuthContent}>
                <div className={css.notAuthIcon}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                </div>
                <h3 className={css.notAuthTitle}>{t("Sign in to manage your subscription")}</h3>
                <p className={css.notAuthText}>
                 {t("Please log in to view and manage your subscription details, billing information, and account settings.")}
                </p>
                <button className={css.signInBtn} onClick={handleSignIn}>
                  {t("signin")}
                </button>
              </div>
            ) : (
              /* Authenticated View */
              <>
            <p className={css.infoText}>
              {t("You can manage your billing information and subscription here")}
            </p>

            {isLoadingSubscription ? (
              <div style={{textAlign: 'center', padding: '40px 20px'}}>
                <p style={{color: '#999'}}>{t("Loading subscription details...")}</p>
              </div>
            ) : currentSubscription && isActiveSubscription ? (
              <>
                {/* Current Plan Card with Badge */}
                <div className={`${css.planCard}`}>
                  {/* Status Badge */}
                  
                  
                  <div className={css.planInfo}>
                    <div className={css.planName}>
                      {t("You're on the")} <span>{currentSubscription.product.name}</span> {t("plan")}
                    </div>
                    <button className={css.upgradeBtn} onClick={handleUpgradePlan}>
                      {t("Upgrade your plan")}
                    </button>
                  </div>
                  <div className={css.nextPayment}>
                    <span className={css.nextPaymentLabel}>{t("Next renewal:")} {nextRenewalLabel}</span>
                    <span className={css.nextPaymentAmount}>₴{parseFloat(currentSubscription.product.price).toFixed(0)}</span>
                  </div>
                </div>

                {/* Plan Details */}
                <div className={css.detailsSection}>
                  <div className={css.detailRow}>
                    <span className={css.detailLabel}>{t("Plan cost")}</span>
                    <span className={css.detailValue}>₴{parseFloat(currentSubscription.product.price).toFixed(0)}/{Number(currentSubscription.product.duration_days) === 1 ? 'pack' : 'month'}</span>
                  </div>
                  <div className={css.detailRow}>
                    <span className={css.detailLabel}>{t("Status")}</span>
                    <span className={css.detailValue} style={{color: isActiveSubscription ? '#28a745' : '#ffc107'}}>{currentSubscription.status_display}</span>
                  </div>
                  <div className={css.detailRow}>
                    <span className={css.detailLabel}>{t("Auto-renewal")}</span>
                    <span className={css.detailValue} style={{color: isActiveSubscription ? '#28a745' : '#999'}}>{isActiveSubscription ? t("Active") : t("Inactive")}</span>
                  </div>
                </div>
              </>
            ) : (
              <div className={css.planCard}>
                  <div className={css.planInfo}>
                    <div className={css.planName}>
                      {t("You're on the")} <span>{t("Free")}</span> {t("plan")}
                    </div>
                    
                  </div>
                </div>
            )}

              {/* Cancel/Upgrade Subscription */}
              {currentSubscription && isActiveSubscription ? (
                <button className={css.cancelSubscriptionBtn} onClick={handleCancelSubscription}>
                  {t("Cancel Subscription")}
                </button>
              ) : currentSubscription ? (
                <button style={{width: "100%"}} className={css.upgradeBtn} onClick={handleUpgradePlan}>
                  {t("Upgrade your plan")}
                </button>
              ) : (
                <button style={{width: "100%"}} className={css.upgradeBtn} onClick={handleUpgradePlan}>
                  {t("Upgrade your plan")}
                </button>
              )}
              
            </>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <div className={`${css.toast} ${showToast ? css.show : ''}`}>
        <div className={css.toastIcon}>✓</div>
        <span>{toastMessage}</span>
      </div>
    </>
  );
};

export default SubModal;